import React, { useEffect, useState, useCallback } from 'react';
import { Table, Spinner, Alert, Form, Row, Col, Button, Pagination, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import useAuthHeaders from '../../hooks/useAuthHeaders';

const API_BASE = process.env.REACT_APP_API_URL || 'https://localhost:5001/api/v1';
const PAGE_SIZE = 50;

const toDateInput = (d) => d.toISOString().slice(0, 10);

const today = new Date();
const defaultDateFrom = toDateInput(new Date(today.getFullYear(), today.getMonth(), 1));
const defaultDateTo = toDateInput(today);

const CFDI_STATUS_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: 'Emitida', label: 'Emitida' },
  { value: 'Error', label: 'Error' },
  { value: 'Omitida', label: 'Omitida' },
  { value: 'SinFactura', label: 'Sin factura' },
];

const PAYMENT_STATUS_LABELS = {
  paid: { label: 'Pagada', bg: 'success' },
  open: { label: 'Pendiente', bg: 'warning' },
  void: { label: 'Anulada', bg: 'secondary' },
  uncollectible: { label: 'Incobrable', bg: 'danger' },
};

const CFDI_STATUS_LABELS = {
  Emitida: { label: 'Emitida', bg: 'success' },
  Error: { label: 'Error', bg: 'danger' },
  Omitida: { label: 'Omitida', bg: 'secondary' },
};

// RFC genérico del SAT para la factura global a público en general.
const RFC_PUBLICO = 'XAXX010101000';

const COLUMNS = [
  { key: 'date',          label: 'Fecha',        sortable: true },
  { key: 'email',         label: 'Email',        sortable: true },
  { key: 'userUuid',      label: 'UserUuid',     sortable: false },
  { key: 'razonsocial',   label: 'Razón Social', sortable: true },
  { key: 'rfc',           label: 'RFC',          sortable: false },
  { key: 'invoiceNumber', label: 'Número',       sortable: false },
  { key: 'amount',        label: 'Monto',        sortable: true },
  { key: 'paymentstatus', label: 'Pago',         sortable: true },
  { key: 'cfdistatus',    label: 'CFDI',         sortable: true },
  { key: 'xml',           label: 'XML',          sortable: false },
  { key: 'pdf',           label: 'PDF',          sortable: false },
  { key: 'acciones',      label: 'Acciones',     sortable: false },
];

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('es-MX', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const formatAmount = (amount, currency) => {
  return (amount || 0).toLocaleString('es-MX', {
    style: 'currency',
    currency: currency ? currency.toUpperCase() : 'MXN',
  });
};

const SortIcon = ({ col, sortBy, sortDir }) => {
  if (sortBy !== col) return <span style={{ opacity: 0.3, marginLeft: 4 }}>↕</span>;
  return <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
};

const PaymentBadge = ({ status }) => {
  const info = PAYMENT_STATUS_LABELS[status] || { label: status || '—', bg: 'light' };
  return <Badge bg={info.bg} className={info.bg === 'warning' || info.bg === 'light' ? 'text-dark' : undefined}>{info.label}</Badge>;
};

const CfdiBadge = ({ status, errorMessage }) => {
  if (!status) return <Badge bg="warning" className="text-dark">Sin factura</Badge>;
  const info = CFDI_STATUS_LABELS[status] || { label: status, bg: 'light' };
  return (
    <Badge bg={info.bg} className={info.bg === 'light' ? 'text-dark' : undefined} title={status === 'Error' ? errorMessage || undefined : undefined}>
      {info.label}
    </Badge>
  );
};

const DownloadCell = ({ ok, busy, onClick }) => {
  if (!ok) return <span style={{ color: '#adb5bd' }}>—</span>;
  return (
    <Button
      variant="link"
      size="sm"
      className="p-0"
      disabled={busy}
      onClick={onClick}
      title="Descargar"
      style={{ textDecoration: 'none' }}
    >
      ⬇
    </Button>
  );
};

const AdminInvoicesReport = () => {
  const { getAuthHeaders } = useAuthHeaders();
  const navigate = useNavigate();

  const [dateFromInput, setDateFromInput] = useState(defaultDateFrom);
  const [dateToInput, setDateToInput] = useState(defaultDateTo);
  const [emailInput, setEmailInput] = useState('');
  const [userUuidInput, setUserUuidInput] = useState('');
  const [razonSocialInput, setRazonSocialInput] = useState('');
  const [cfdiStatusInput, setCfdiStatusInput] = useState('');

  const [query, setQuery] = useState({
    dateFrom: defaultDateFrom,
    dateTo: defaultDateTo,
    email: '',
    userUuid: '',
    razonSocial: '',
    cfdiStatus: '',
    sortBy: 'date',
    sortDir: 'desc',
    page: 1,
    pageSize: PAGE_SIZE,
  });

  const [result, setResult] = useState({ items: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // StripeInvoiceId de la fila que está generando/descargando, y aviso de resultado por acción.
  const [rowBusy, setRowBusy] = useState(null);
  const [rowNotice, setRowNotice] = useState(null);

  const fetchReport = useCallback(async (q) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams();
      if (q.dateFrom)     params.set('dateFrom', q.dateFrom);
      if (q.dateTo)       params.set('dateTo', q.dateTo);
      if (q.email)        params.set('email', q.email);
      if (q.userUuid)     params.set('userUuid', q.userUuid);
      if (q.razonSocial)  params.set('razonSocial', q.razonSocial);
      if (q.cfdiStatus)   params.set('cfdiStatus', q.cfdiStatus);
      params.set('sortBy',   q.sortBy);
      params.set('sortDir',  q.sortDir);
      params.set('page',     q.page);
      params.set('pageSize', q.pageSize);

      const response = await fetch(`${API_BASE}/admin/reports/invoices?${params}`, { headers });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchReport(query);
  }, [query, fetchReport]);

  const handleSearch = () => {
    setQuery(prev => ({
      ...prev,
      dateFrom: dateFromInput,
      dateTo: dateToInput,
      email: emailInput,
      userUuid: userUuidInput,
      razonSocial: razonSocialInput,
      cfdiStatus: cfdiStatusInput,
      page: 1,
    }));
  };

  const handleClear = () => {
    setDateFromInput(defaultDateFrom);
    setDateToInput(defaultDateTo);
    setEmailInput('');
    setUserUuidInput('');
    setRazonSocialInput('');
    setCfdiStatusInput('');
    setQuery(prev => ({
      ...prev,
      dateFrom: defaultDateFrom,
      dateTo: defaultDateTo,
      email: '',
      userUuid: '',
      razonSocial: '',
      cfdiStatus: '',
      page: 1,
    }));
  };

  const handleSort = (col) => {
    setQuery(prev => ({
      ...prev,
      sortBy: col,
      sortDir: prev.sortBy === col && prev.sortDir === 'asc' ? 'desc' : 'asc',
      page: 1,
    }));
  };

  const handlePage = (p) => setQuery(prev => ({ ...prev, page: p }));

  const handleGenerarPublico = async (row) => {
    const ok = window.confirm(
      `¿Generar la factura a público en general (${RFC_PUBLICO}) para el pago ` +
      `${row.invoiceNumber || row.stripeInvoiceId}?\n\n` +
      `Esto ocupará el registro de esta factura: el cliente ya no podrá solicitar ` +
      `su CFDI a su propio RFC después.`
    );
    if (!ok) return;

    setRowBusy(row.stripeInvoiceId);
    setRowNotice(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/admin/reports/invoices/${encodeURIComponent(row.stripeInvoiceId)}/factura-publico`,
        { method: 'POST', headers }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || data.error || `Error ${response.status}`);
      }
      setRowNotice({ type: 'success', text: `Factura a público generada (UUID ${data.cfdiUuid || '—'}).` });
      fetchReport(query);
    } catch (err) {
      setRowNotice({ type: 'danger', text: `No se pudo generar la factura a público: ${err.message}` });
    } finally {
      setRowBusy(null);
    }
  };

  const handleDownload = async (row, tipo) => {
    setRowBusy(row.stripeInvoiceId);
    setRowNotice(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/admin/reports/invoices/${encodeURIComponent(row.stripeInvoiceId)}/factura/download`,
        { headers }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || data.error || `Error ${response.status}`);
      const url = tipo === 'xml' ? data.xmlUrl : data.pdfUrl;
      if (!url) throw new Error(`No hay ${tipo.toUpperCase()} disponible para esta factura.`);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      setRowNotice({ type: 'danger', text: `No se pudo descargar el ${tipo.toUpperCase()}: ${err.message}` });
    } finally {
      setRowBusy(null);
    }
  };

  const totalPages = result.totalPages ?? 0;
  const currentPage = result.page ?? 1;

  const buildPageItems = () => {
    const items = [];
    const delta = 2;
    const left = Math.max(1, currentPage - delta);
    const right = Math.min(totalPages, currentPage + delta);
    if (left > 1) {
      items.push(1);
      if (left > 2) items.push('...');
    }
    for (let i = left; i <= right; i++) items.push(i);
    if (right < totalPages) {
      if (right < totalPages - 1) items.push('...');
      items.push(totalPages);
    }
    return items;
  };

  return (
    <div className="status-container">
      <div className="page-header mb-4">
        <div>
          <Button
            variant="link"
            size="sm"
            className="p-0 mb-1 text-muted"
            onClick={() => navigate('/admin/reports')}
          >
            ← Reportes
          </Button>
          <h2 className="page-title">Facturas emitidas</h2>
          <p className="page-subtitle text-muted">
            {result.totalCount} factura{result.totalCount !== 1 ? 's' : ''} encontrada{result.totalCount !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-3 p-3 border rounded bg-light">
        <Row className="g-2 align-items-end">
          <Col xs={6} md={2}>
            <Form.Label className="small mb-1">Fecha desde</Form.Label>
            <Form.Control
              size="sm"
              type="date"
              value={dateFromInput}
              onChange={e => setDateFromInput(e.target.value)}
            />
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-1">Fecha hasta</Form.Label>
            <Form.Control
              size="sm"
              type="date"
              value={dateToInput}
              onChange={e => setDateToInput(e.target.value)}
            />
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-1">Email</Form.Label>
            <Form.Control
              size="sm"
              placeholder="Filtrar por email..."
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-1">UserUuid</Form.Label>
            <Form.Control
              size="sm"
              placeholder="UUID exacto..."
              value={userUuidInput}
              onChange={e => setUserUuidInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-1">Razón Social</Form.Label>
            <Form.Control
              size="sm"
              placeholder="Filtrar por razón social..."
              value={razonSocialInput}
              onChange={e => setRazonSocialInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-1">Estado CFDI</Form.Label>
            <Form.Select
              size="sm"
              value={cfdiStatusInput}
              onChange={e => setCfdiStatusInput(e.target.value)}
            >
              {CFDI_STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={12} md={2} className="d-flex gap-1">
            <Button size="sm" variant="primary" onClick={handleSearch} className="w-100">
              Buscar
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={handleClear} title="Limpiar">
              ✕
            </Button>
          </Col>
        </Row>
      </div>

      {error && <Alert variant="danger">Error al cargar reporte: {error}</Alert>}

      {rowNotice && (
        <Alert variant={rowNotice.type} dismissible onClose={() => setRowNotice(null)}>
          {rowNotice.text}
        </Alert>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" style={{ color: '#13a0ff' }} />
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <Table hover size="sm" className="mb-2">
              <thead className="table-light">
                <tr>
                  {COLUMNS.map(({ key, label, sortable }) => (
                    <th
                      key={key}
                      onClick={sortable ? () => handleSort(key) : undefined}
                      style={{ cursor: sortable ? 'pointer' : 'default', whiteSpace: 'nowrap', userSelect: 'none' }}
                    >
                      {label}
                      {sortable && <SortIcon col={key} sortBy={query.sortBy} sortDir={query.sortDir} />}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.items.map((row) => (
                  <tr key={row.stripeInvoiceId}>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDate(row.paidAtUtc || row.createdAtUtc)}</td>
                    <td>{row.email ?? '—'}</td>
                    <td>
                      <span
                        title={row.userUuid || undefined}
                        style={{ display: 'inline-block', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}
                      >
                        {row.userUuid ?? '—'}
                      </span>
                    </td>
                    <td>{row.razonSocial ?? '—'}</td>
                    <td>{row.rfc ?? '—'}</td>
                    <td>{row.invoiceNumber ?? '—'}</td>
                    <td className="text-end" style={{ whiteSpace: 'nowrap' }}>{formatAmount(row.amount, row.currency)}</td>
                    <td><PaymentBadge status={row.paymentStatus} /></td>
                    <td>
                      <CfdiBadge status={row.cfdiStatus} errorMessage={row.errorMessage} />
                      {row.rfc === RFC_PUBLICO && (
                        <Badge bg="info" className="text-dark ms-1">Público</Badge>
                      )}
                    </td>
                    <td className="text-center">
                      <DownloadCell ok={row.hasXml} busy={rowBusy === row.stripeInvoiceId} onClick={() => handleDownload(row, 'xml')} />
                    </td>
                    <td className="text-center">
                      <DownloadCell ok={row.hasPdf} busy={rowBusy === row.stripeInvoiceId} onClick={() => handleDownload(row, 'pdf')} />
                    </td>
                    <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                      {row.cfdiStatus !== 'Emitida' && (
                        <Button
                          size="sm"
                          variant="outline-primary"
                          disabled={rowBusy === row.stripeInvoiceId}
                          onClick={() => handleGenerarPublico(row)}
                          title="Generar CFDI a público en general (XAXX010101000)"
                        >
                          {rowBusy === row.stripeInvoiceId
                            ? <Spinner as="span" animation="border" size="sm" />
                            : 'Facturar a público'}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {result.items.length === 0 && (
                  <tr>
                    <td colSpan={COLUMNS.length} className="text-center text-muted py-4">
                      No se encontraron facturas con los filtros aplicados.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-2">
              <small className="text-muted">
                Página {currentPage} de {totalPages} ({result.totalCount} resultados)
              </small>
              <Pagination size="sm" className="mb-0">
                <Pagination.Prev disabled={currentPage === 1} onClick={() => handlePage(currentPage - 1)} />
                {buildPageItems().map((item, i) =>
                  item === '...'
                    ? <Pagination.Ellipsis key={`e${i}`} disabled />
                    : <Pagination.Item key={item} active={item === currentPage} onClick={() => handlePage(item)}>{item}</Pagination.Item>
                )}
                <Pagination.Next disabled={currentPage === totalPages} onClick={() => handlePage(currentPage + 1)} />
              </Pagination>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminInvoicesReport;
